import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../../Assets/Images/Logo.svg";
import "./Login.scss";
import { useAuth } from "../../context/AuthContext";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import {
  isSignInWithEmailLink,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithEmailLink,
} from "firebase/auth";
import { auth } from "../../lib/firebase";
import {
  signInWithGooglePopupSmart,
  signInWithMicrosoftPopupSmart,
  firebaseSignOut,
} from "../../lib/firebaseAuth";
import { requireI9ApiBase, requireAuthApiBase } from "../../lib/utils";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const COUNTRIES = [
  { code: "AF", dialCode: "+93",  name: "Afghanistan" },
  { code: "AL", dialCode: "+355", name: "Albania" },
  { code: "DZ", dialCode: "+213", name: "Algeria" },
  { code: "AR", dialCode: "+54",  name: "Argentina" },
  { code: "AU", dialCode: "+61",  name: "Australia" },
  { code: "AT", dialCode: "+43",  name: "Austria" },
  { code: "BD", dialCode: "+880", name: "Bangladesh" },
  { code: "BE", dialCode: "+32",  name: "Belgium" },
  { code: "BR", dialCode: "+55",  name: "Brazil" },
  { code: "CA", dialCode: "+1",   name: "Canada" },
  { code: "CN", dialCode: "+86",  name: "China" },
  { code: "CO", dialCode: "+57",  name: "Colombia" },
  { code: "EG", dialCode: "+20",  name: "Egypt" },
  { code: "FR", dialCode: "+33",  name: "France" },
  { code: "DE", dialCode: "+49",  name: "Germany" },
  { code: "GH", dialCode: "+233", name: "Ghana" },
  { code: "GR", dialCode: "+30",  name: "Greece" },
  { code: "IN", dialCode: "+91",  name: "India" },
  { code: "ID", dialCode: "+62",  name: "Indonesia" },
  { code: "IR", dialCode: "+98",  name: "Iran" },
  { code: "IQ", dialCode: "+964", name: "Iraq" },
  { code: "IE", dialCode: "+353", name: "Ireland" },
  { code: "IL", dialCode: "+972", name: "Israel" },
  { code: "IT", dialCode: "+39",  name: "Italy" },
  { code: "JP", dialCode: "+81",  name: "Japan" },
  { code: "JO", dialCode: "+962", name: "Jordan" },
  { code: "KE", dialCode: "+254", name: "Kenya" },
  { code: "KR", dialCode: "+82",  name: "South Korea" },
  { code: "KW", dialCode: "+965", name: "Kuwait" },
  { code: "LB", dialCode: "+961", name: "Lebanon" },
  { code: "MY", dialCode: "+60",  name: "Malaysia" },
  { code: "MX", dialCode: "+52",  name: "Mexico" },
  { code: "MA", dialCode: "+212", name: "Morocco" },
  { code: "NL", dialCode: "+31",  name: "Netherlands" },
  { code: "NZ", dialCode: "+64",  name: "New Zealand" },
  { code: "NG", dialCode: "+234", name: "Nigeria" },
  { code: "NO", dialCode: "+47",  name: "Norway" },
  { code: "PK", dialCode: "+92",  name: "Pakistan" },
  { code: "PH", dialCode: "+63",  name: "Philippines" },
  { code: "PL", dialCode: "+48",  name: "Poland" },
  { code: "PT", dialCode: "+351", name: "Portugal" },
  { code: "QA", dialCode: "+974", name: "Qatar" },
  { code: "RO", dialCode: "+40",  name: "Romania" },
  { code: "RU", dialCode: "+7",   name: "Russia" },
  { code: "SA", dialCode: "+966", name: "Saudi Arabia" },
  { code: "SG", dialCode: "+65",  name: "Singapore" },
  { code: "ZA", dialCode: "+27",  name: "South Africa" },
  { code: "ES", dialCode: "+34",  name: "Spain" },
  { code: "LK", dialCode: "+94",  name: "Sri Lanka" },
  { code: "SE", dialCode: "+46",  name: "Sweden" },
  { code: "CH", dialCode: "+41",  name: "Switzerland" },
  { code: "TW", dialCode: "+886", name: "Taiwan" },
  { code: "TH", dialCode: "+66",  name: "Thailand" },
  { code: "TR", dialCode: "+90",  name: "Turkey" },
  { code: "UA", dialCode: "+380", name: "Ukraine" },
  { code: "AE", dialCode: "+971", name: "UAE" },
  { code: "GB", dialCode: "+44",  name: "United Kingdom" },
  { code: "US", dialCode: "+1",   name: "United States" },
  { code: "VN", dialCode: "+84",  name: "Vietnam" },
];
const DEFAULT_COUNTRY = COUNTRIES.find((c) => c.code === "US") ?? COUNTRIES[0];

const PHONE_RULES = {
  AF: { min: 9,  max: 9  }, AL: { min: 9,  max: 9  }, DZ: { min: 9,  max: 9  },
  AR: { min: 10, max: 10 }, AU: { min: 9,  max: 9  }, AT: { min: 10, max: 11 },
  BD: { min: 10, max: 10 }, BE: { min: 9,  max: 9  }, BR: { min: 10, max: 11 },
  CA: { min: 10, max: 10 }, CN: { min: 11, max: 11 }, CO: { min: 10, max: 10 },
  EG: { min: 10, max: 10 }, FR: { min: 9,  max: 9  }, DE: { min: 10, max: 11 },
  GH: { min: 9,  max: 9  }, GR: { min: 10, max: 10 }, IN: { min: 10, max: 10 },
  ID: { min: 9,  max: 12 }, IR: { min: 10, max: 10 }, IQ: { min: 10, max: 10 },
  IE: { min: 9,  max: 9  }, IL: { min: 9,  max: 9  }, IT: { min: 9,  max: 10 },
  JP: { min: 10, max: 11 }, JO: { min: 9,  max: 9  }, KE: { min: 9,  max: 9  },
  KR: { min: 9,  max: 10 }, KW: { min: 8,  max: 8  }, LB: { min: 7,  max: 8  },
  MY: { min: 9,  max: 10 }, MX: { min: 10, max: 10 }, MA: { min: 9,  max: 9  },
  NL: { min: 9,  max: 9  }, NZ: { min: 8,  max: 9  }, NG: { min: 10, max: 10 },
  NO: { min: 8,  max: 8  }, PK: { min: 10, max: 10 }, PH: { min: 10, max: 10 },
  PL: { min: 9,  max: 9  }, PT: { min: 9,  max: 9  }, QA: { min: 8,  max: 8  },
  RO: { min: 9,  max: 9  }, RU: { min: 10, max: 10 }, SA: { min: 9,  max: 9  },
  SG: { min: 8,  max: 8  }, ZA: { min: 9,  max: 9  }, ES: { min: 9,  max: 9  },
  LK: { min: 9,  max: 9  }, SE: { min: 9,  max: 9  }, CH: { min: 9,  max: 9  },
  TW: { min: 9,  max: 9  }, TH: { min: 9,  max: 9  }, TR: { min: 10, max: 10 },
  UA: { min: 9,  max: 9  }, AE: { min: 9,  max: 9  }, GB: { min: 10, max: 10 },
  US: { min: 10, max: 10 }, VN: { min: 9,  max: 10 },
};

const getSsoProvider = async (email) => {
  const domain = email.split("@")[1]?.toLowerCase().trim() || "";
  if (!domain) return "unknown";

  if (domain === "gmail.com" || domain.endsWith(".googlemail.com")) return "google";

  if (["outlook.com", "hotmail.com", "live.com", "msn.com"].includes(domain)) return "microsoft";

  try {
    const res = await fetch(
      `https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=MX`
    );
    if (!res.ok) return "unknown";
    const data = await res.json().catch(() => null);
    const records = Array.isArray(data?.Answer)
      ? data.Answer.map((a) => String(a?.data || "").toLowerCase()).join(" ")
      : "";

    if (!records) return "unknown";
    if (records.includes("google.com")) return "google";
    if (
      records.includes("outlook.com") ||
      records.includes("protection.outlook.com") ||
      records.includes("microsoft.com") ||
      records.includes("office365.com") ||
      records.includes("onmicrosoft.com")
    ) return "microsoft";
  } catch {
    // ignore DNS lookup failures
  }

  return "unknown";
};

const isSafeRedirectUrl = (url) => {
  if (!url) return false;
  try {
    const target = new URL(url, window.location.origin);
    return target.origin === window.location.origin;
  } catch {
    return false;
  }
};

const checkEverifyAccess = async (email) => {
  try {
    const encoded = encodeURIComponent(email.trim());
    const res = await fetch(
      `${requireI9ApiBase()}/user/checkeverifyuseraccess?email=${encoded}`,
      { method: "GET", headers: { accept: "application/json" } }
    );
    if (!res.ok) return false;
    const data = await res.json().catch(() => null);
    return Boolean(
      data === true ||
        data?.result?.exists === true ||
        data?.hasAccess === true ||
        data?.access === true ||
        data?.isVerified === true
    );
  } catch {
    return false;
  }
};

const Login = () => {
  const { isAuthenticated, loading: authLoading, user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loginHint, setLoginHint] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("login_hint") || localStorage.getItem("lastUpn") || "";
  });
  const [submitting, setSubmitting] = useState(false);
  const [authError, setAuthError] = useState("");
  const [emailLinkInProgress, setEmailLinkInProgress] = useState(false);
  const [emailLinkError, setEmailLinkError] = useState("");
  const [isEmailLinkFlow, setIsEmailLinkFlow] = useState(
    () => isSignInWithEmailLink(auth, window.location.href)
  );
  const [accountConflict, setAccountConflict] = useState(null);

  // Phone auth state
  const [phoneAuthOpen, setPhoneAuthOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [phoneAuthStep, setPhoneAuthStep] = useState("idle");
  const [phoneAuthError, setPhoneAuthError] = useState("");
  const [phoneAuthLoading, setPhoneAuthLoading] = useState(false);

  // Email OTP state
  const [emailOtpOpen, setEmailOtpOpen] = useState(false);
  const [emailOtpRequestedEmail, setEmailOtpRequestedEmail] = useState("");
  const [emailOtpCode, setEmailOtpCode] = useState("");
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState("");
  const emailOtpRefs = useRef([]);

  // Country picker state
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [localPhone, setLocalPhone] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef(null);
  const recaptchaRef = useRef(null);
  const confirmationRef = useRef(null);

  const phoneRule = PHONE_RULES[selectedCountry.code] ?? { min: 7, max: 15 };
  const fullPhoneNumber = `${selectedCountry.dialCode}${localPhone.replace(/\D/g, "")}`;
  const isPhoneValid =
    localPhone.replace(/\D/g, "").length >= phoneRule.min &&
    localPhone.replace(/\D/g, "").length <= phoneRule.max;
  const filteredCountries = COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.dialCode.includes(countrySearch)
  );

  const hasAuthRedirectParams =
    typeof window !== "undefined" &&
    (() => {
      const params = new URLSearchParams(window.location.search);
      return (
        params.has("oobCode") ||
        params.get("mode") === "signIn" ||
        params.has("apiKey") ||
        params.has("redirect_url") ||
        params.has("email")
      );
    })();

  const redirectPath = location.state?.from?.pathname || "/";

  // Redirect when authenticated — skip if an email sign-in link is still being processed,
  // or if an account-conflict modal needs to be resolved first.
  useEffect(() => {
    if (isAuthenticated && !emailLinkInProgress && !isEmailLinkFlow && !accountConflict) {
      const pendingRedirect =
        sessionStorage.getItem("pending_redirect_url") ||
        new URLSearchParams(window.location.search).get("redirect_url");
      sessionStorage.removeItem("pending_redirect_url");
      if (pendingRedirect && isSafeRedirectUrl(pendingRedirect)) {
        window.location.href = pendingRedirect;
        return;
      }
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectPath, emailLinkInProgress, isEmailLinkFlow, accountConflict]);

  // Email-link completion flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isEmailLink = isSignInWithEmailLink(auth, window.location.href);
    const redirectUrl = params.get("redirect_url") || "";
    setIsEmailLinkFlow(isEmailLink);

    if (!isEmailLink) return;

    if (redirectUrl) {
      sessionStorage.setItem("pending_redirect_url", redirectUrl);
    }

    const email =
      params.get("email") || window.localStorage.getItem("emailForSignIn");
    if (!email) {
      setEmailLinkError("Missing email for email-link sign-in.");
      return;
    }

    setEmailLinkInProgress(true);
    signInWithEmailLink(auth, email, window.location.href)
      .then(() => {
        window.localStorage.removeItem("emailForSignIn");
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsEmailLinkFlow(false);
      })
      .catch(() => {
        setIsEmailLinkFlow(false);
        window.history.replaceState({}, document.title, window.location.pathname);
        setEmailLinkError("This invite link is invalid or has already been used. Please log in below.");
      })
      .finally(() => {
        setEmailLinkInProgress(false);
      });
  }, []);

  // If login_hint is present and a different user is already signed in, surface a
  // conflict modal instead of silently signing them out from under them.
  useEffect(() => {
    if (authLoading) return;
    const urlLoginHint = new URLSearchParams(window.location.search).get("login_hint");
    if (!urlLoginHint || !isAuthenticated) return;
    const authUserEmail = user?.username?.toLowerCase().trim();
    if (authUserEmail && authUserEmail !== urlLoginHint.toLowerCase().trim()) {
      setAccountConflict({ existingEmail: authUserEmail });
    }
  }, [authLoading, isAuthenticated, user]);

  // Phone recaptcha init
  useEffect(() => {
    if (!phoneAuthOpen) return;
    setupRecaptcha().catch((err) => {
      console.error("Failed to initialize reCAPTCHA.", err);
    });
  }, [phoneAuthOpen]);

  // Lock body scroll when email OTP modal is open
  useEffect(() => {
    if (!emailOtpOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => emailOtpRefs.current[0]?.focus(), 0);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [emailOtpOpen]);

  // Close country dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isEmailValid = EMAIL_REGEX.test(loginHint);

  const sendEmailOtp = async (emailAddress) => {
    if (!emailAddress) return;
    setEmailOtpLoading(true);
    setEmailOtpError("");
    try {
      const res = await fetch(
        `${requireAuthApiBase()}/api/v1/auth/send-otp`,
        {
          method: "POST",
          headers: { accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ email: emailAddress }),
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Failed to send OTP.");
      }
      setEmailOtpRequestedEmail(emailAddress);
      setEmailOtpCode("");
      setEmailOtpOpen(true);
    } catch (err) {
      setAuthError(err?.message || "Failed to send OTP. Please try again.");
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (!emailOtpRequestedEmail) return;
    if (!emailOtpCode.trim()) {
      setEmailOtpError("Enter the 4-digit OTP.");
      return;
    }
    setEmailOtpLoading(true);
    setIsVerifyingOtp(true);
    setEmailOtpError("");
    let succeeded = false;
    try {
      const res = await fetch(
        `${requireAuthApiBase()}/api/v1/auth/verify-otp`,
        {
          method: "POST",
          headers: { accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({
            email: emailOtpRequestedEmail,
            otp: emailOtpCode.trim(),
            application_name: "everify",
          }),
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "OTP verification failed.");
      }
      const redirectUrl = data?.sign_in_link || data?.redirecturl || data?.redirectUrl;
      if (redirectUrl) {
        succeeded = true;
        window.location.href = redirectUrl;
        return;
      }
    } catch (err) {
      setEmailOtpError(err?.message || "OTP verification failed.");
    } finally {
      setEmailOtpLoading(false);
      if (!succeeded) setIsVerifyingOtp(false);
    }
  };

  const handleEmailOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = emailOtpCode.split("");
    next[index] = digit;
    const merged = next.join("").slice(0, 4);
    setEmailOtpCode(merged);
    if (digit && emailOtpRefs.current[index + 1]) {
      emailOtpRefs.current[index + 1]?.focus();
    }
  };

  const handleEmailOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !emailOtpCode[index] && emailOtpRefs.current[index - 1]) {
      emailOtpRefs.current[index - 1]?.focus();
    }
  };

  const handleEmailOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!pasted) return;
    setEmailOtpCode(pasted);
    const nextIndex = Math.min(pasted.length, 3);
    emailOtpRefs.current[nextIndex]?.focus();
  };

  const handleContinue = async () => {
    if (!isEmailValid || submitting) return;
    localStorage.setItem("lastUpn", loginHint);
    setSubmitting(true);
    setAuthError("");
    try {
      const allowed = await checkEverifyAccess(loginHint);
      if (!allowed) {
        setAuthError("Access not found for this email.");
        return;
      }
      const provider = await getSsoProvider(loginHint);
      if (provider === "google") {
        await signInWithGooglePopupSmart(loginHint);
      } else if (provider === "microsoft") {
        await signInWithMicrosoftPopupSmart(loginHint);
      } else {
        // Unknown domain — use email OTP flow
        setSubmitting(false);
        await sendEmailOtp(loginHint);
        return;
      }
    } catch (err) {
      const code = err?.code || "";
      if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
        setAuthError(err?.message || "Sign-in failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError("");
    setSubmitting(true);
    try {
      const result = await signInWithGooglePopupSmart(loginHint);
      const popupEmail = result?.user?.email || auth.currentUser?.email || "";
      if (popupEmail) {
        const allowed = await checkEverifyAccess(popupEmail);
        if (!allowed) {
          await firebaseSignOut();
          setAuthError("Access not found for this email.");
          return;
        }
      }
    } catch (err) {
      const code = err?.code || "";
      if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
        setAuthError(err?.message || "Google sign-in failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setAuthError("");
    setSubmitting(true);
    try {
      const result = await signInWithMicrosoftPopupSmart(loginHint);
      const popupEmail = result?.user?.email || auth.currentUser?.email || "";
      if (popupEmail) {
        const allowed = await checkEverifyAccess(popupEmail);
        if (!allowed) {
          await firebaseSignOut();
          setAuthError("Access not found for this email.");
          return;
        }
      }
    } catch (err) {
      const code = err?.code || "";
      if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
        setAuthError(err?.message || "Microsoft sign-in failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const setupRecaptcha = async () => {
    const container = document.getElementById("recaptcha-container");
    if (!container) throw new Error("reCAPTCHA container not found.");
    if (recaptchaRef.current) return recaptchaRef.current;

    recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "invisible",
      callback: () => {},
      "expired-callback": () => {},
    });

    await recaptchaRef.current.render();
    window.recaptchaVerifier = recaptchaRef.current;
    return recaptchaRef.current;
  };

  const handleSendOtp = async () => {
    if (!isPhoneValid) {
      setPhoneAuthError("Enter a valid phone number.");
      return;
    }
    setPhoneAuthError("");
    setPhoneAuthLoading(true);
    try {
      const verifier = await setupRecaptcha();
      const confirmation = await signInWithPhoneNumber(auth, fullPhoneNumber, verifier);
      confirmationRef.current = confirmation;
      window.confirmationResult = confirmation;
      setPhoneAuthStep("code-sent");
    } catch (err) {
      setPhoneAuthError(err?.message || "Failed to send OTP. Please try again.");
      try {
        if (recaptchaRef.current) recaptchaRef.current.clear();
      } catch {}
      recaptchaRef.current = null;
    } finally {
      setPhoneAuthLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) {
      setPhoneAuthError("Enter the 6-digit OTP.");
      return;
    }
    if (!confirmationRef.current) {
      setPhoneAuthError("OTP session expired. Please request a new code.");
      return;
    }
    setPhoneAuthError("");
    setPhoneAuthLoading(true);
    try {
      await confirmationRef.current.confirm(otpCode.trim());
      setOtpCode("");
      setPhoneAuthStep("idle");
    } catch (err) {
      setPhoneAuthError(err?.message || "Invalid OTP. Please try again.");
    } finally {
      setPhoneAuthLoading(false);
    }
  };

  if (authLoading || emailLinkInProgress || isEmailLinkFlow) {
    return (
      <LoadingSpinner message={(emailLinkInProgress || isEmailLinkFlow) ? "Signing you in..." : "Loading..."} />
    );
  }

  if (hasAuthRedirectParams && !isAuthenticated) {
    return <LoadingSpinner message="Signing you in..." />;
  }

  return (
    <div className="login-page">
      {/* Navbar */}
      <nav className="login-navbar">
        <div className="login-navbar-inner">
          <img src={Logo} alt="Canary" className="login-nav-logo" />
        </div>
      </nav>

      <div className="login-wrapper">
      <div className="login-shell">
        <div className="form-pane">
          <div className="form-inner">
            <div className="brand-block">
              <h1 className="brand-heading">Create Your Account</h1>
              <p className="brand-sub">Set up your credential to get started</p>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); handleContinue(); }}
              style={{ marginBottom: "1.5rem" }}
            >
              <div className="hint-input">
                <label className="input-label" htmlFor="email">
                  Personal Email
                </label>
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  placeholder="name@company.com"
                  value={loginHint}
                  onChange={(e) => {
                    setLoginHint(e.target.value.trim());
                    setAuthError("");
                  }}
                  aria-label="Work email address"
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                className={`cta ${!submitting && !emailOtpLoading ? "cta-enabled" : "cta-disabled"}`}
                disabled={!isEmailValid || submitting || emailOtpLoading}
                style={{ marginTop: "1rem" }}
              >
                {submitting || emailOtpLoading ? (
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <svg style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} viewBox="0 0 24 24" fill="none">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Redirecting...
                  </span>
                ) : "Continue"}
              </button>
            </form>

            <div className="divider">
              <span>or continue with</span>
            </div>

            <div className="social-row" style={{ marginTop: "1.5rem" }}>
              <button
                type="button"
                className="social-btn"
                onClick={handleGoogleLogin}
                disabled={submitting}
                aria-label="Login with Google"
              >
                <svg style={{ width: "20px", height: "20px", flexShrink: 0 }} viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span>Login with Google</span>
              </button>
              <button
                type="button"
                className="social-btn"
                onClick={handleMicrosoftLogin}
                disabled={submitting}
                aria-label="Login with Microsoft"
              >
                <svg style={{ width: "20px", height: "20px", flexShrink: 0 }} viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
                <span>Login with Microsoft</span>
              </button>
            </div>

            {authError && (
              <p style={{ color: "#dc2626", fontSize: "0.875rem", marginTop: "1rem", textAlign: "center" }} role="alert">
                {authError}
              </p>
            )}

            <p className="terms">
              By creating an account, you agree to our{" "}
              <a href="https://canaryai.com/terms" target="_blank" rel="noreferrer">
                Terms of Use
              </a>{" "}
              and{" "}
              <a href="https://canaryai.com/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </p>

            <div className="phone-auth">
              <button
                type="button"
                className="phone-toggle"
                onClick={() => setPhoneAuthOpen((v) => !v)}
              >
                Continue with phone
              </button>

              {phoneAuthOpen && (
                <div className="phone-panel">
                  <div id="recaptcha-container" />

                  <div className="phone-country-row" ref={countryDropdownRef}>
                    <button
                      type="button"
                      className="country-btn"
                      onClick={() => {
                        setCountryDropdownOpen((v) => !v);
                        setCountrySearch("");
                      }}
                      aria-label="Select country code"
                    >
                      <img
                        src={`https://flagcdn.com/w20/${selectedCountry.code.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png 2x`}
                        width="20"
                        height="15"
                        alt={selectedCountry.name}
                        className="country-flag"
                      />
                      <span className="country-dial">{selectedCountry.dialCode}</span>
                      <svg
                        className={`country-chevron ${countryDropdownOpen ? "rotated" : ""}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>

                    {countryDropdownOpen && (
                      <div className="country-dropdown-panel">
                        <input
                          type="text"
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          placeholder="Search country..."
                          className="country-search"
                          autoFocus
                        />
                        <ul className="country-list">
                          {filteredCountries.length ? (
                            filteredCountries.map((c) => (
                              <li
                                key={c.code}
                                className={`country-item ${c.code === selectedCountry.code ? "active" : ""}`}
                                onClick={() => {
                                  setSelectedCountry(c);
                                  setPhoneAuthError("");
                                  setCountryDropdownOpen(false);
                                  setCountrySearch("");
                                }}
                              >
                                <img
                                  src={`https://flagcdn.com/w20/${c.code.toLowerCase()}.png`}
                                  srcSet={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png 2x`}
                                  width="20"
                                  height="15"
                                  alt={c.name}
                                  className="country-flag"
                                />
                                <span className="country-name">{c.name}</span>
                                <span className="country-code-label">{c.dialCode}</span>
                              </li>
                            ))
                          ) : (
                            <li className="country-item-empty">No results</li>
                          )}
                        </ul>
                      </div>
                    )}

                    <input
                      type="tel"
                      inputMode="numeric"
                      placeholder={"0".repeat(phoneRule.min)}
                      value={localPhone}
                      maxLength={phoneRule.max}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, phoneRule.max);
                        setLocalPhone(digits);
                        setPhoneAuthError("");
                      }}
                      className="phone-number-input"
                    />
                  </div>

                  {phoneAuthStep === "code-sent" && (
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter OTP"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      className="phone-input"
                    />
                  )}

                  {phoneAuthError && (
                    <p className="phone-error">{phoneAuthError}</p>
                  )}

                  {phoneAuthStep === "idle" ? (
                    <button
                      type="button"
                      disabled={phoneAuthLoading || !isPhoneValid}
                      onClick={handleSendOtp}
                      className="phone-cta"
                    >
                      {phoneAuthLoading ? "Sending OTP..." : "Send OTP"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={phoneAuthLoading}
                      onClick={handleVerifyOtp}
                      className="phone-cta"
                    >
                      {phoneAuthLoading ? (
                        <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                          <svg style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} viewBox="0 0 24 24" fill="none">
                            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Verifying...
                        </span>
                      ) : "Verify OTP"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="art-pane">
          <img
            src="/login.svg"
            alt="Login Illustration"
            className="art-img desktop"
          />
        </div>
      </div>
      </div>

      {/* Email OTP Modal */}
      {emailOtpOpen && (
        <div
          className="email-otp-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-otp-title"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setEmailOtpOpen(false);
              setEmailOtpError("");
            }
          }}
          tabIndex={-1}
        >
          <div className="email-otp-modal">
            <button
              type="button"
              className="email-otp-close"
              onClick={() => { setEmailOtpOpen(false); setEmailOtpError(""); }}
              aria-label="Close"
            >
              ×
            </button>

            <div className="email-otp-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            </div>

            <div className="email-otp-header">
              <h3 id="email-otp-title" className="email-otp-title">Verify Your Email</h3>
              <p className="email-otp-sub">
                We've sent a 4-digit code to{" "}
                <strong>{emailOtpRequestedEmail}</strong>
              </p>
            </div>

            <div className="email-otp-body">
              <p className="email-otp-label">Enter verification code</p>
              <div className="email-otp-digits">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <input
                    key={`otp-${idx}`}
                    ref={(el) => { emailOtpRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={emailOtpCode[idx] || ""}
                    onChange={(e) => handleEmailOtpChange(idx, e.target.value)}
                    onKeyDown={(e) => handleEmailOtpKeyDown(idx, e)}
                    onPaste={handleEmailOtpPaste}
                    className={`email-otp-digit${emailOtpError ? " error" : ""}`}
                  />
                ))}
              </div>

              {emailOtpError && (
                <p className="email-otp-error" role="alert">{emailOtpError}</p>
              )}

              <button
                type="button"
                disabled={emailOtpLoading || emailOtpCode.length < 4}
                onClick={verifyEmailOtp}
                className="email-otp-verify"
              >
                {isVerifyingOtp ? (
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <svg style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} viewBox="0 0 24 24" fill="none">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Verifying...
                  </span>
                ) : "Verify & Continue"}
              </button>

              <div className="email-otp-resend-row">
                <span className="email-otp-resend-hint">Didn't receive the code?</span>
                <button
                  type="button"
                  disabled={emailOtpLoading || isVerifyingOtp}
                  onClick={() => sendEmailOtp(emailOtpRequestedEmail)}
                  className="email-otp-resend"
                >
                  {emailOtpLoading && !isVerifyingOtp ? "Sending..." : "Resend code"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account conflict modal */}
      {accountConflict && (
        <div
          className="email-otp-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="account-conflict-title"
          tabIndex={-1}
        >
          <div className="email-otp-modal">
            <div className="email-otp-header">
              <h3 id="account-conflict-title" className="email-otp-title">Already Signed In</h3>
              <p className="email-otp-sub">
                You're already signed in as{" "}
                <strong>{accountConflict.existingEmail}</strong> in this browser.
                Please log out of that account before signing in with a different one.
              </p>
            </div>
            <div className="email-otp-body">
              <button
                type="button"
                className="email-otp-verify"
                onClick={async () => {
                  await signOut();
                  setAccountConflict(null);
                }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="login-footer">
        <div className="login-footer-inner">
          <div className="login-footer-brand">
            <span className="login-footer-powered">Powered by</span>
            <img src="/Logowhite.svg" alt="Canary" className="login-footer-logo" />
          </div>
          <div className="login-footer-links">
            <a href="https://canaryai.com/privacy" target="_blank" rel="noreferrer" className="login-footer-link">
              Privacy Policy
            </a>
            <a href="https://canaryai.com/terms" target="_blank" rel="noreferrer" className="login-footer-link">
              Terms of Use
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
